/**
 * VTodoParser.ts
 *
 * VTodo 파싱을 담당하는 모듈 (ICalendar - VTodo)
 *
 * 주요 기능:
 * - ICS 데이터에서 VTodo 파싱
 * - VTodo를 TodoItem 객체로 변환
 * - VALARM 파싱 및 처리
 */

import moment from 'moment-timezone'
import { parseICS } from 'node-ical'
import { RRule } from 'rrule'
import { TodoFolder } from './TodoFolder'
import { priorityNumberToPriority, TaskReminder, TodoItem, todoStatusToStatus } from './TodoItem'
import { createRRuleWithTimezone } from './TodoManager'

/**
 * ICS 데이터에서 VTODO를 파싱하여 TodoItem 배열로 변환
 * @param icsData ICS 문자열 데이터
 * @param folder TodoFolder 정보
 * @param url Todo 항목의 URL
 * @param etag Todo 항목의 ETag
 * @returns TodoItem 객체 또는 null
 */
export function parseVTodoFromICS(
  icsData: string,
  folder: TodoFolder,
  url: string,
  etag: string,
): TodoItem | null {
  try {
    const vCalendar = parseICS(icsData)

    // VTODO 항목 찾기
    const vtodoEntry = Object.entries(vCalendar).find(([, v]) => (v as any).type === 'VTODO')

    if (!vtodoEntry) {
      return null
    }

    const [, vtodo] = vtodoEntry

    return parseVTodoToTodoItem(vtodo, folder, url, etag)
  } catch (error) {
    console.error('Error parsing VTODO ICS:', error)
    return null
  }
}

/**
 * 파싱된 VTODO 객체를 TodoItem으로 변환
 * @param vtodo 파싱된 VTODO 객체
 * @param folder TodoFolder 정보
 * @param url Todo 항목의 URL
 * @param etag Todo 항목의 ETag
 * @returns TodoItem 객체
 */
export function parseVTodoToTodoItem(
  vtodo: any,
  folder: TodoFolder,
  url: string,
  etag: string,
): TodoItem {
  const todoItem: TodoItem = {
    url: url, // sync id
    uid: null,
    folderUrl: folder.url,
    etag: etag,
    summary: null,
    description: null,
    status: null,
    priority: null,
    percentComplete: 0,
    startDate: null,
    dtstamp: null,
    dueDate: null,
    location: null,
    completedDate: null,
    recurrenceRule: null,
    recurrenceRuleString: null,
    recurrenceId: null,
    datetype: null,
    createdDate: null,
    lastModifiedDate: null,
    datatype: null,
    alarms: [],
    author: null,
    unknownProperties: [],
  }

  const valarms = []

  // Extract timezone information from DTSTART or DUE
  let extractedTimezone: string | null = null

  // VTODO 속성들을 TodoItem으로 매핑
  Object.keys(vtodo).forEach((key) => {
    const value = vtodo[key]

    switch (key) {
      case 'uid':
        todoItem.uid = value
        break
      case 'priority':
        todoItem.priority = priorityNumberToPriority(value)
        break
      case 'due':
        todoItem.dueDate = parseICalendarDate(value) || null
        // Extract timezone from DUE if available
        if (value && typeof value === 'object' && value.params?.TZID) {
          extractedTimezone = value.params.TZID
        }
        break
      case 'start':
        todoItem.startDate = parseICalendarDate(value) || null
        // Extract timezone from DTSTART if available
        if (value && typeof value === 'object' && value.params?.TZID) {
          extractedTimezone = value.params.TZID
        }
        break
      case 'dtstamp':
        todoItem.dtstamp = value || null
        break
      case 'completion':
        todoItem.percentComplete = value || 0
        break
      case 'rrule':
        if (value) {
          try {
            // node-ical 0.25.6의 RRule wrapper(rrule-temporal)는 rrule.js의 RRule 클래스가
            // 아니므로 instanceof 체크가 false가 되어 RRULE이 통째로 손실되었다.
            // wrapper든 native든 toString()으로 RRULE 라인을 추출해 fromString으로
            // 일관되게 rrule.js native 인스턴스로 정규화한다.
            let rruleString: string | null = null

            if (typeof value === 'string') {
              rruleString = value.replace(/^RRULE:/, '')
            } else if (
              value &&
              typeof (value as { toString?: () => string }).toString === 'function'
            ) {
              const str = (value as { toString: () => string }).toString()
              const rruleLine = str.split('\n').find((line) => line.startsWith('RRULE:'))
              if (rruleLine) {
                rruleString = rruleLine.replace(/^RRULE:/, '')
              }
            }

            if (rruleString) {
              if (extractedTimezone) {
                const startDateForRRule = todoItem.startDate || todoItem.dueDate
                todoItem.recurrenceRule = createRRuleWithTimezone(
                  rruleString,
                  startDateForRRule,
                  extractedTimezone,
                )
              } else {
                todoItem.recurrenceRule = RRule.fromString(`RRULE:${rruleString}`)
              }
              todoItem.recurrenceRuleString = rruleString
            } else {
              console.warn('Unknown RRULE format:', value)
              todoItem.recurrenceRule = null
              todoItem.recurrenceRuleString = null
            }
          } catch (error) {
            console.warn('Failed to parse RRULE:', value, error)
            todoItem.recurrenceRule = null
            todoItem.recurrenceRuleString = null
          }
        } else {
          todoItem.recurrenceRule = null
          todoItem.recurrenceRuleString = null
        }
        break
      case 'completed':
        todoItem.completedDate = value || null
        break
      case 'description':
        todoItem.description = value || null
        break
      case 'location':
        todoItem.location = value || null
        break
      case 'status':
        todoItem.status = todoStatusToStatus(value)
        break
      case 'summary':
        todoItem.summary = value || null
        break
      case 'type':
        todoItem.datatype = value || null
        break
      case 'created':
        todoItem.createdDate = value || null
        break
      case 'lastmodified':
        todoItem.lastModifiedDate = value || null
        break
      case 'recurrence-id':
        todoItem.recurrenceId = parseICalendarDate(value) || null
        break
      case 'alarms':
        todoItem.alarms = parseAlarms(value)
        break
      case 'datetype':
        todoItem.datetype = value || null
        break
      case 'NI-AUTHOR':
        todoItem.author = value || null
        break
      default:
        // VALARM 처리
        if (key.length === 36 && value.type === 'VALARM' && value.action === 'DISPLAY') {
          valarms.push(value)
        } else {
          // unknownProperties 에는 추가만 해놓지만, 사용하지 않도록 한다.
          todoItem.unknownProperties.push({ key, value })
        }
        break
    }
  })

  // VALARM 파싱
  if (valarms.length > 0) {
    todoItem.alarms = parseAlarms(valarms)
  }

  return todoItem
}

/**
 * 여러 ICS 데이터에서 모든 VTODO를 파싱하여 TodoItem 딕셔너리로 변환
 * @param todoObjects CalDAV에서 가져온 todo 객체 배열
 * @param folder TodoFolder 정보
 * @returns TodoItem 딕셔너리 (URL을 키로 사용)
 */
export function parseVTodos(
  todoObjects: Array<{ url: string; etag: string; data: string }>,
  folder: TodoFolder,
): Record<string, TodoItem> {
  const todoDict: Record<string, TodoItem> = {}

  todoObjects.forEach((todo) => {
    const todoItem = parseVTodoFromICS(todo.data, folder, todo.url, todo.etag)
    if (todoItem) {
      todoDict[todo.url] = todoItem
      console.log(`[VTODO] 파싱 완료: ${todoItem.summary || todoItem.uid || todo.url}`)
    } else {
      console.warn(`[VTODO] 파싱 실패: ${todo.url}`)
    }
  })

  return todoDict
}

/**
 * VALARM 배열을 TaskReminder 배열로 파싱
 * @param rawAlarms 원시 VALARM 데이터 배열
 * @returns TaskReminder 배열
 */
function parseAlarms(rawAlarms: any[]): TaskReminder[] {
  let trigger: string | { params: Record<string, string>; val: string }

  return rawAlarms
    .filter((alarm) => alarm.type === 'VALARM' && alarm.action === 'DISPLAY')
    .map((alarm) => {
      if (typeof alarm.trigger === 'string') {
        trigger = alarm.trigger
        return {
          action: alarm.action,
          end: alarm.end,
          trigger: trigger,
        }
      } else if (
        typeof alarm.trigger === 'object' &&
        alarm.trigger !== null &&
        typeof alarm.trigger.val === 'string'
      ) {
        trigger = {
          params: alarm.trigger.params || {},
          val: alarm.trigger.val,
        }
        return {
          action: alarm.action,
          end: alarm.end,
          trigger: trigger,
          uid: alarm.uid,
        }
      }
      return null
    })
    .filter(Boolean) as TaskReminder[]
}

/**
 * iCalendar 날짜 형식을 JavaScript Date로 변환
 * Calendar 모듈의 RefineEvent.ts 참고
 * @param dateValue iCalendar 날짜 값 (문자열, 객체, 또는 Date)
 * @returns Date 객체 또는 null
 */
export function parseICalendarDate(dateValue: any): Date | null {
  if (!dateValue) return null

  // 이미 Date 객체인 경우
  if (dateValue instanceof Date) return dateValue

  // 객체 형태인 경우 (예: {params: {TZID: 'Asia/Seoul'}, val: '20230918T170000'})
  if (typeof dateValue === 'object' && dateValue.val) {
    const dateString = dateValue.val
    const tzid = dateValue.params?.TZID

    // 타임존 정보가 있는 경우
    if (tzid && dateString.includes('T')) {
      try {
        return moment.tz(dateString, 'YYYYMMDDTHHmmss', tzid).toDate()
      } catch (error) {
        console.warn(`타임존 파싱 실패: ${tzid}, UTC로 처리`, error)
        return moment.utc(dateString, 'YYYYMMDDTHHmmss').toDate()
      }
    }
    // 타임존 정보가 없는 경우 (UTC로 처리)
    else if (dateString.includes('T')) {
      return moment.utc(dateString, 'YYYYMMDDTHHmmss').toDate()
    }
    // 날짜만 있는 경우 (로컬 날짜로 처리)
    else if (dateString.length === 8) {
      return moment(dateString, 'YYYYMMDD').toDate()
    }
  }

  // 문자열인 경우
  if (typeof dateValue === 'string') {
    // YYYYMMDDTHHMMSS 형식 (예: '20230918T170000')
    if (dateValue.includes('T')) {
      return moment(dateValue, 'YYYYMMDDTHHmmss').toDate()
    }
    // YYYYMMDD 형식 (예: '20230918')
    else if (dateValue.length === 8) {
      return moment(dateValue, 'YYYYMMDD').toDate()
    }
    // ISO 형식이나 기타 형식
    else {
      const parsed = moment(dateValue)
      return parsed.isValid() ? parsed.toDate() : null
    }
  }

  return null
}
